import React, { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { Field } from "./booking/Field";
import DatePickerField from "./booking/DatePickerField";
import BookingSuccess from "./booking/BookingSuccess";
import { PRICING, initialForm, validateBooking } from "./booking/bookingConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "rounded-none border-[#1A1F24]/20 h-12";

const readPrefill = () => {
  try {
    const raw = sessionStorage.getItem("hrm_prefill");
    if (!raw) return null;
    sessionStorage.removeItem("hrm_prefill");
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const BookingForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const applyPrefill = () => {
      const prefill = readPrefill();
      if (prefill) {
        setForm((f) => ({ ...f, ...prefill }));
        toast.success(lang === "es" ? "Cotización aplicada" : "Quote applied");
      }
    };
    applyPrefill();
    window.addEventListener("hrm:prefill", applyPrefill);
    return () => window.removeEventListener("hrm:prefill", applyPrefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const price = useMemo(
    () => PRICING[form.service_type]?.[form.vehicle_type] ?? 0,
    [form.service_type, form.vehicle_type]
  );

  const isRoundTrip = form.service_type === "round-trip";

  const submitBooking = async () => {
    const payload = {
      ...form,
      pickup_date: format(form.pickup_date, "yyyy-MM-dd"),
      return_date: form.return_date ? format(form.return_date, "yyyy-MM-dd") : null,
    };
    const { data } = await axios.post(`${API}/bookings`, payload);
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateBooking(form, lang);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitBooking();
      setSuccess(data);
      toast.success(t.booking.successTitle);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const fallback = lang === "es" ? "No se pudo enviar" : "Could not submit";
      toast.error(typeof detail === "string" ? detail : fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSuccess(null);
    setForm(initialForm);
  };

  if (success) {
    return <BookingSuccess booking={success} t={t} lang={lang} onReset={handleReset} />;
  }

  return (
    <form
      data-testid="booking-form"
      onSubmit={handleSubmit}
      className="bg-white border border-[#EAE6DF] p-8 sm:p-12"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label={t.booking.service}>
          <Select value={form.service_type} onValueChange={(v) => setField("service_type", v)}>
            <SelectTrigger data-testid="service-type-select" className={inputCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(t.booking.serviceTypes).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`service-option-${k}`}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t.booking.vehicle}>
          <Select value={form.vehicle_type} onValueChange={(v) => setField("vehicle_type", v)}>
            <SelectTrigger data-testid="vehicle-type-select" className={inputCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(t.booking.vehicles).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`vehicle-option-${k}`}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t.booking.pickup}>
          <Input
            data-testid="pickup-input"
            value={form.pickup_location}
            onChange={(e) => setField("pickup_location", e.target.value)}
            placeholder={t.booking.pickupPh}
            className={inputCls}
            required
          />
        </Field>

        <Field label={t.booking.dropoff}>
          <Input
            data-testid="dropoff-input"
            value={form.dropoff_location}
            onChange={(e) => setField("dropoff_location", e.target.value)}
            placeholder={t.booking.dropoffPh}
            className={inputCls}
            required
          />
        </Field>

        <Field label={t.booking.date}>
          <DatePickerField
            testid="pickup-date-picker"
            date={form.pickup_date}
            setDate={(d) => setField("pickup_date", d)}
            placeholder={t.booking.pickDate}
          />
        </Field>

        <Field label={t.booking.time}>
          <Input
            data-testid="pickup-time-input"
            type="time"
            value={form.pickup_time}
            onChange={(e) => setField("pickup_time", e.target.value)}
            className={inputCls}
            required
          />
        </Field>

        {isRoundTrip && (
          <>
            <Field label={t.booking.returnDate}>
              <DatePickerField
                testid="return-date-picker"
                date={form.return_date}
                setDate={(d) => setField("return_date", d)}
                placeholder={t.booking.pickDate}
              />
            </Field>
            <Field label={t.booking.returnTime}>
              <Input
                data-testid="return-time-input"
                type="time"
                value={form.return_time}
                onChange={(e) => setField("return_time", e.target.value)}
                className={inputCls}
              />
            </Field>
          </>
        )}

        <Field label={t.booking.passengers}>
          <Input
            data-testid="passengers-input"
            type="number"
            min={1}
            max={20}
            value={form.passengers}
            onChange={(e) => setField("passengers", parseInt(e.target.value || "1", 10))}
            className={inputCls}
            required
          />
        </Field>

        <Field label={t.booking.luggage}>
          <Input
            data-testid="luggage-input"
            type="number"
            min={0}
            max={30}
            value={form.luggage}
            onChange={(e) => setField("luggage", parseInt(e.target.value || "0", 10))}
            className={inputCls}
          />
        </Field>

        <Field label={t.booking.flight}>
          <Input
            data-testid="flight-input"
            value={form.flight_number}
            onChange={(e) => setField("flight_number", e.target.value)}
            placeholder={t.booking.flightPh}
            className={inputCls}
          />
        </Field>

        <Field label={t.booking.airline}>
          <Input
            data-testid="airline-input"
            value={form.airline}
            onChange={(e) => setField("airline", e.target.value)}
            placeholder={t.booking.airlinePh}
            className={inputCls}
          />
        </Field>

        <Field label={t.booking.name}>
          <Input
            data-testid="name-input"
            value={form.customer_name}
            onChange={(e) => setField("customer_name", e.target.value)}
            className={inputCls}
            required
          />
        </Field>

        <Field label={t.booking.email}>
          <Input
            data-testid="email-input"
            type="email"
            value={form.customer_email}
            onChange={(e) => setField("customer_email", e.target.value)}
            className={inputCls}
            required
          />
        </Field>

        <Field label={t.booking.phone} full>
          <Input
            data-testid="phone-input"
            value={form.customer_phone}
            onChange={(e) => setField("customer_phone", e.target.value)}
            className={inputCls}
            required
          />
        </Field>

        <Field label={t.booking.requests} full>
          <Textarea
            data-testid="requests-input"
            value={form.special_requests}
            onChange={(e) => setField("special_requests", e.target.value)}
            placeholder={t.booking.requestsPh}
            className="rounded-none border-[#1A1F24]/20 min-h-[100px]"
          />
        </Field>
      </div>

      <div className="mt-10 pt-8 border-t border-[#EAE6DF] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A5568] mb-1">
            {t.booking.estimate}
          </p>
          <p className="font-serif text-4xl text-[#1A1F24]" data-testid="estimated-price">
            ${price.toFixed(2)} <span className="text-base text-[#4A5568]">USD</span>
          </p>
        </div>
        <button
          data-testid="submit-booking-btn"
          type="submit"
          disabled={submitting}
          className="bg-[#2E4F4F] hover:bg-[#1F3636] text-white px-10 py-4 text-xs uppercase tracking-[0.25em] disabled:opacity-60 transition-colors"
        >
          {submitting ? t.booking.submitting : t.booking.submit}
        </button>
      </div>
      <p className="mt-4 text-xs text-[#4A5568] italic">{t.booking.payNote}</p>
    </form>
  );
};

export default BookingForm;
